/**
 * The particle field behind the about page.
 *
 * The quietest of the four: no shapes, just a deep volume of dust that drifts
 * on its own and streams past as the page scrolls, with nearer points moving
 * faster than far ones. All of the motion is in the vertex shader, so the
 * positions are written once; only the colours are rewritten, on a theme
 * change.
 */
import * as THREE from 'three';
import { PALETTE } from './particleField';
import { prefersReducedMotion } from '../../../shared/motion';

export type AboutField = { dispose: () => void };

type Options = { canvas: HTMLCanvasElement };

const COUNT = 16000;

export function createAboutField({ canvas }: Options): AboutField {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 120);
  camera.position.z = 9;

  const position = new Float32Array(COUNT * 3);
  const seed = new Float32Array(COUNT * 4);
  const color = new Float32Array(COUNT * 3);
  const size = new Float32Array(COUNT);
  for (let i = 0; i < COUNT; i++) {
    const r = [Math.random(), Math.random(), Math.random(), Math.random()];
    seed.set(r, i * 4);
    position.set([(r[2] - 0.5) * 36, (r[3] - 0.5) * 40, -1 - Math.random() * 16], i * 3);
    size[i] = Math.random() < 0.07 ? 3.2 : 1.1 + Math.random() * 1.5;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
  geometry.setAttribute('aR', new THREE.BufferAttribute(seed, 4));
  geometry.setAttribute('aColor', new THREE.BufferAttribute(color, 3));
  geometry.setAttribute('aSize', new THREE.BufferAttribute(size, 1));

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uPR: { value: renderer.getPixelRatio() },
      uOp: { value: 1 },
      uLight: { value: 0 },
      uScroll: { value: 0 },
    },
    // Scrolling wraps each point around a 40-unit column at its own speed.
    vertexShader: `uniform float uTime; uniform float uPR; uniform float uScroll; attribute vec4 aR; attribute vec3 aColor; attribute float aSize; varying vec3 vC; varying float vA;
      void main(){ vec3 p = position; float sp = 0.15 + aR.x*0.35;
        p.y = mod(p.y + uScroll*(0.6 + aR.y*1.4) + 20.0, 40.0) - 20.0;
        p.x += sin(uTime*sp + aR.z*6.28)*0.35; p.y += cos(uTime*sp*0.8 + aR.w*6.28)*0.25;
        vA = 0.7 + 0.3*sin(uTime*(0.6 + aR.x) + aR.y*30.0);
        vec4 mv = modelViewMatrix*vec4(p, 1.0); gl_PointSize = aSize*uPR*(26.0/-mv.z); gl_Position = projectionMatrix*mv; vC = aColor; }`,
    fragmentShader:
      'varying vec3 vC; varying float vA; uniform float uOp; uniform float uLight; void main(){ float d = length(gl_PointCoord - 0.5); if (d > 0.5) discard; float a = smoothstep(0.5, 0.0, d)*vA*uOp; gl_FragColor = vec4(mix(vC*a, vC, uLight), a); }',
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const points = new THREE.Points(geometry, material);
  // The shader moves every point, so the stored bounds mean nothing.
  points.frustumCulled = false;
  scene.add(points);

  const paint = (light: boolean) => {
    const palette = (light ? PALETTE.light : PALETTE.dark).map((c) => new THREE.Color(c));
    for (let i = 0; i < COUNT; i++) {
      const pick = seed[i * 4];
      const c = pick < 0.4 ? palette[0] : pick < 0.8 ? palette[1] : palette[2];
      const b = light ? 0.9 : 0.7 + seed[i * 4 + 1] * 0.3;
      color.set([c.r * b, c.g * b, c.b * b], i * 3);
    }
    geometry.attributes.aColor.needsUpdate = true;
    material.blending = light ? THREE.NormalBlending : THREE.AdditiveBlending;
    material.uniforms.uLight.value = light ? 1 : 0;
    material.uniforms.uOp.value = light ? 0.9 : 1;
    material.needsUpdate = true;
  };

  // ThemeProvider owns the `dark` class on <html>; read it every frame.
  const isLight = () => !document.documentElement.classList.contains('dark');
  let light: boolean | null = null;

  const still = prefersReducedMotion();
  const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
  const onMove = (e: PointerEvent) => {
    mouse.tx = e.clientX / window.innerWidth - 0.5;
    mouse.ty = e.clientY / window.innerHeight - 0.5;
  };
  const onResize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('resize', onResize);
  onResize();

  let scroll = 0;
  let raf = 0;
  const t0 = performance.now();

  const frame = () => {
    const time = still ? 0 : (performance.now() - t0) / 1000;
    if (isLight() !== light) {
      light = isLight();
      paint(light);
    }

    scroll += ((window.scrollY / window.innerHeight) * 3 - scroll) * 0.08;
    material.uniforms.uTime.value = time;
    material.uniforms.uScroll.value = scroll;

    mouse.x += (mouse.tx - mouse.x) * 0.04;
    mouse.y += (mouse.ty - mouse.y) * 0.04;
    camera.position.x = mouse.x * 1.2;
    camera.position.y = -mouse.y * 0.8;
    camera.lookAt(0, 0, -8);
    points.rotation.z = Math.sin(time * 0.05) * 0.05;

    renderer.render(scene, camera);
    raf = requestAnimationFrame(frame);
  };
  frame();

  return {
    dispose() {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('resize', onResize);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    },
  };
}
